import { Injectable } from '@angular/core';
import { ServiceEvaluatorService } from 'shared/services/service-evaluator/service-evaluator.service';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { BcConfigService } from 'common/config/provider';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { BcSettingsService } from 'common/settings/settings.service';
import { ServiceTypeModel } from 'common/common/constants/service-types';
import { CommonAuthService } from 'common/auth/common-auth.service';

@Injectable()
export class ServiceAvailabilityCollectorService {
  private _bcEnums = bcEnums;
  private _workItemTypes = WorkItemTypes;
  private _services: number[];
  private _servicesIncludedInOmniQueue: number[] = [];

  constructor(private _serviceEvaluator: ServiceEvaluatorService,
              private _bcConfig: BcConfigService,
              private _bcSettingsService: BcSettingsService,
              private _commonAuth: CommonAuthService) {
  }

  public async getAvailableServicesByServiceTypeValue(): Promise<ServiceTypeModel[]> {
    return <ServiceTypeModel[]> await this._getAvailableServicesBy('serviceType');
  }

  public async getAvailableServicesByWorkItemTypeValue(): Promise<WorkItemTypes[]> {
    return this._getAvailableServicesBy('workItemType');
  }

  public isServiceEnabled(service: number): Promise<boolean> {
    switch (service) {
      case this._bcEnums.services.CHATS:
        return this._isChatEnabled;
      case this._bcEnums.services.SMS:
        return this._isSmsEnabled;
      case this._bcEnums.services.FACEBOOK:
        return this._isFacebookEnabled;
      case this._bcEnums.services.ASYNC_MESSAGE:
        return this._isAsyncMessageEnabled;
      case this._bcEnums.services.EMAILS:
        return this._isEmailEnabled;
      default:
        return Promise.resolve(false);
    }
  }

  public servicesIncludedInOmni(): number[] {
    return this._servicesIncludedInOmniQueue;
  }

  private async _getAvailableServicesBy(valueType: string): Promise<ServiceTypeModel[] | WorkItemTypes[]> {
    await this._getServices();
    await this._collectOmniSettings();

    let services = this._services.filter(service => !this._isChannelIncludedInOmni(service));

    if (await this._isOmniEnabledForAgent() && this._servicesIncludedInOmniQueue.length) {
      services.unshift(this._bcEnums.services.OMNI);
    }

    return valueType === 'serviceType'
      ? services.map(item => ({ serviceType: item }))
      : services.map(item => this._serviceTypeConverter(item));
  }

  private async _isServiceTypeEnabled(serviceType): Promise<boolean> {
    return await this._commonAuth.isBoldEnabled() && this._serviceEvaluator.isServiceEnabled(serviceType) && this._serviceEvaluator.hasAccessForService(serviceType);
  }

  private async _collectOmniSettings(): Promise<void> {
    this._servicesIncludedInOmniQueue = [];
    if (!(await this._commonAuth.isBoldEnabled())) {
      return;
    }
    return this._bcSettingsService.getSettings().then(settings => {
      if (settings.omniQueueSettings) {
        settings.omniQueueSettings.includedServiceTypes.forEach(async service => {
          if (await this.isServiceEnabled(service)) {
            this._servicesIncludedInOmniQueue.push(service);
          }
        });
      }
    });
  }

  private async _isOmniEnabledForAgent(): Promise<boolean> {
    return this._bcConfig.current.features.omniQueue && await this._isServiceTypeEnabled(this._bcEnums.services.OMNI);
  }

  private _isChannelIncludedInOmni(channelName): boolean {
    if (this._servicesIncludedInOmniQueue) {
      return !!(this._servicesIncludedInOmniQueue.find(channel => channel === channelName));
    }
  }

  private async _getServices(): Promise<void> {
    if (this._services) {
      return;
    }

    this._services = [];
    if (await this._isChatEnabled) {
      this._services.push(this._bcEnums.services.CHATS);
    }
    if (await this._isSmsEnabled) {
      this._services.push(this._bcEnums.services.SMS);
    }
    if (await this._isFacebookEnabled) {
      this._services.push(this._bcEnums.services.FACEBOOK);
    }
    if (await this._isAsyncMessageEnabled) {
      this._services.push(this._bcEnums.services.ASYNC_MESSAGE);
    }
    if (await this._isEmailEnabled) {
      this._services.push(this._bcEnums.services.EMAILS);
    }
  }

  private _serviceTypeConverter(serviceType): WorkItemTypes {
    switch (serviceType) {
      case this._bcEnums.services.CHATS:
        return this._workItemTypes.CHAT;
      case this._bcEnums.services.SMS:
        return this._workItemTypes.SMS;
      case this._bcEnums.services.FACEBOOK:
        return this._workItemTypes.FACEBOOK;
      case this._bcEnums.services.ASYNC_MESSAGE:
        return this._workItemTypes.ASYNC_MESSAGE;
      case this._bcEnums.services.EMAILS:
        return this._workItemTypes.EMAIL;
      case this._bcEnums.services.OMNI:
        return this._workItemTypes.OMNI;
      default:
        return serviceType;
    }
  }

  private get _isChatEnabled(): Promise<boolean> {
    return this._isServiceTypeEnabled(this._bcEnums.services.CHATS);
  }

  private get _isSmsEnabled(): Promise<boolean> {
    return this._bcConfig.current.features.smsMessages && this._isServiceTypeEnabled(this._bcEnums.services.SMS);
  }

  private get _isFacebookEnabled(): Promise<boolean> {
    return this._isServiceTypeEnabled(this._bcEnums.services.FACEBOOK);
  }

  private get _isAsyncMessageEnabled(): Promise<boolean> {
    return this._bcConfig.current.features.asyncMessage && this._isServiceTypeEnabled(this._bcEnums.services.ASYNC_MESSAGE);
  }

  private get _isEmailEnabled(): Promise<boolean> {
    return this._isServiceTypeEnabled(this._bcEnums.services.EMAILS);
  }
}
